// /invite/:token: where a super-admin invitation's "Accept invitation" button lands
// on Ops (registration plan S2; the league admin's equivalent is the tenant
// site's /admin/invite/:token).
//
// Same flow as the league page: it says who invited them and offers BOTH
// "Sign in" and "Create an account", never asking the server, and never
// showing, whether the address already has an account. The token stays in
// the path through either choice.
//   Sign in:  POST /auth/login WITHOUT portal (they aren't a super admin yet),
//             then POST /auth/invite/claim with that new token; the stored Ops
//             session is untouched until adoptSession accepts the result.
//   Create:   POST /auth/invite/accept, then adoptSession.
// A league invite opened here is a dead end: it belongs to the league's admin.

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { api } from '@/api'
import { isApiError, type ApiError } from '@/api/client'
import type { InviteLookup } from '@/api/types'
import { useAuth } from '@/auth/useAuth'
import { Button, ButtonLink, FormField, InlineAlert, LoadingState, TextInput } from '@/components/ui'
import { cn } from '@/lib/format'
import { useDocumentTitle } from '@/lib/hooks'
import { AuthCard, authLinkClass, useNoReferrer } from './AuthCard'

type DeadReason = 'not_found' | 'used' | 'expired' | 'revoked' | 'inactive' | 'league'
type Phase =
  | { kind: 'loading' }
  | { kind: 'failed'; message: string }
  | { kind: 'dead'; reason: DeadReason; message?: string }
  | { kind: 'ready'; invite: InviteLookup }

const WAIT = 'Too many attempts. Please wait a minute and try again.'
const ASK = 'Ask a ScoreLM super admin to send you a new invitation.'
const DEAD_TITLE: Record<DeadReason, string> = {
  not_found: 'This invitation is no longer valid',
  used: 'This invitation has already been used',
  expired: 'This invitation has expired',
  revoked: 'This invitation is no longer valid',
  inactive: 'This account can’t join yet',
  league: 'This invitation opens in the league’s admin',
}

function asApiError(e: unknown): ApiError | null {
  return isApiError(e) ? e : null
}

function deadEndFor(err: ApiError | null): { reason: DeadReason } | null {
  if (!err) return null
  if (err.status === 404) return { reason: 'not_found' }
  if (err.status !== 410) return null
  if (err.code === 'invite_used') return { reason: 'used' }
  if (err.code === 'invite_expired') return { reason: 'expired' }
  if (err.code === 'invite_revoked') return { reason: 'revoked' }
  return { reason: 'not_found' }
}

function messageOf(e: unknown): string {
  const err = asApiError(e)
  if (err?.status === 429) return WAIT
  if (e instanceof Error && e.message) return e.message
  return 'Something went wrong. Try again.'
}

export default function InvitePage() {
  useDocumentTitle('Accept invitation')
  useNoReferrer()
  const { token = '' } = useParams()
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const ctrl = new AbortController()
    setPhase({ kind: 'loading' })
    api.auth
      .readInvite(token, ctrl.signal)
      .then(({ invite }) => setPhase(invite.kind === 'league' ? { kind: 'dead', reason: 'league' } : { kind: 'ready', invite }))
      .catch((e) => {
        if (ctrl.signal.aborted) return
        const dead = deadEndFor(asApiError(e))
        setPhase(dead ? { kind: 'dead', ...dead } : { kind: 'failed', message: messageOf(e) })
      })
    return () => ctrl.abort()
  }, [token, tick])

  let body: ReactNode
  if (phase.kind === 'loading') body = <LoadingState label="Checking your invitation…" />
  else if (phase.kind === 'failed')
    body = (
      <div className="grid gap-4">
        <h1 className="text-2xl font-extrabold leading-tight text-ink">Couldn’t open your invitation</h1>
        <InlineAlert tone="bad">{phase.message}</InlineAlert>
        <Button onClick={() => setTick((n) => n + 1)}>Retry</Button>
      </div>
    )
  else if (phase.kind === 'dead') body = <DeadEnd reason={phase.reason} message={phase.message} />
  else body = <Ready token={token} invite={phase.invite} onDead={(reason, message) => setPhase({ kind: 'dead', reason, message })} />

  return <AuthCard footer="Invitations to manage a league open on that league's own site.">{body}</AuthCard>
}

function FocusHeading({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLHeadingElement>(null)
  useEffect(() => ref.current?.focus(), [])
  return (
    <h1 ref={ref} tabIndex={-1} className="text-2xl font-extrabold leading-tight text-ink focus:outline-none">
      {children}
    </h1>
  )
}

function DeadEnd({ reason, message }: { reason: DeadReason; message?: string }) {
  return (
    <div className="grid gap-3">
      <FocusHeading>{DEAD_TITLE[reason]}</FocusHeading>
      {reason === 'league' ? (
        <p className="text-[13.5px] text-muted">This invitation opens in the league&apos;s admin. Use the link in your email.</p>
      ) : reason === 'inactive' ? (
        message ? <p className="text-[13.5px] text-ink">{message}</p> : null
      ) : (
        <p className="text-[13.5px] text-muted">{ASK}</p>
      )}
      {reason === 'used' ? (
        <ButtonLink to="/login" variant="primary" block>
          Sign in
        </ButtonLink>
      ) : null}
    </div>
  )
}

function Ready({ token, invite, onDead }: { token: string; invite: InviteLookup; onDead: (r: DeadReason, message?: string) => void }) {
  const { adoptSession } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [choice, setChoice] = useState<'signin' | 'create' | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<ReactNode | null>(null)
  const [wrong, setWrong] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [email, setEmail] = useState(invite.email)
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [fieldErr, setFieldErr] = useState<{ name?: string | null; password?: string | null }>({})
  const passwordRef = useRef<HTMLInputElement>(null)
  const [focusPw, setFocusPw] = useState(0)
  useEffect(() => {
    if (focusPw) passwordRef.current?.focus()
  }, [focusPw])

  const choose = (c: 'signin' | 'create') => {
    setChoice(c)
    setNotice(null)
    setError(null)
    setWrong(null)
  }

  const common = (e: unknown): boolean => {
    const err = asApiError(e)
    const dead = deadEndFor(err)
    if (dead) {
      onDead(dead.reason)
      return true
    }
    setError(messageOf(e))
    return true
  }

  const submitSignIn = async (e: FormEvent) => {
    e.preventDefault()
    setNotice(null)
    setWrong(null)
    if (!email.trim() || !password) {
      setError('Enter your email and password.')
      return
    }
    setError(null)
    setPending(true)
    const typed = email.trim()
    try {
      let session
      try {
        session = await api.auth.signInForInvite(typed, password)
      } catch (err) {
        const ae = asApiError(err)
        if (ae?.status === 401)
          setError(
            <span className="grid gap-2">
              <span>That didn’t work. Check your email and password, or create an account if you’re new to ScoreLM.</span>
              <span>
                <Button size="sm" onClick={() => choose('create')}>
                  Create an account instead
                </Button>
              </span>
            </span>,
          )
        else setError(messageOf(err))
        return
      }
      let claim
      try {
        claim = await api.auth.claimInvite(token, session.token)
      } catch (err) {
        const ae = asApiError(err)
        if (ae?.status === 403 && ae.code === 'wrong_account') setWrong(typed)
        else if (ae?.status === 403 && ae.code === 'impersonation_forbidden') setError('Exit the impersonation session, then open the invitation again.')
        else if (ae?.status === 401) setError('Sign in again to accept this invitation.')
        else common(err)
        return
      }
      try {
        // Stores the session only when the claim made them a super admin.
        adoptSession(session.token, claim.user ?? session.user)
      } catch (err) {
        setError(messageOf(err))
        return
      }
      navigate('/dashboard', { replace: true })
    } finally {
      setPending(false)
    }
  }

  const submitCreate = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    const errs = { name: name.trim() ? null : 'Add your name.', password: newPassword.length < 8 ? 'Use at least 8 characters.' : null }
    setFieldErr(errs)
    if (errs.name || errs.password) return
    setPending(true)
    try {
      const r = await api.auth.acceptInvite({ token, name: name.trim(), password: newPassword })
      adoptSession(r.token, r.user)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      const ae = asApiError(err)
      if (ae?.status === 409 && ae.code === 'account_exists') {
        setNewPassword('')
        setChoice('signin')
        setEmail(invite.email)
        setPassword('')
        setNotice('You already have an account. Sign in to accept this invitation.')
        setFocusPw((n) => n + 1)
      } else if (ae?.status === 409 && ae.code === 'account_inactive') {
        onDead('inactive', ae.message)
      } else if (ae?.status === 400 && (ae.field === 'name' || ae.field === 'password')) {
        setFieldErr({ [ae.field]: ae.message })
      } else common(err)
    } finally {
      setPending(false)
    }
  }

  const inviter = invite.invited_by?.name ?? 'A ScoreLM super admin'
  return (
    <div className="grid gap-4">
      <div className="grid gap-1.5">
        <FocusHeading>Join ScoreLM</FocusHeading>
        <p className="text-[13.5px] text-ink">
          {inviter} has invited you to join ScoreLM as a super admin. Super admins can manage every league, billing and the other super admins.
        </p>
        <p className="break-words text-xs text-muted [overflow-wrap:anywhere]">
          Invitation for <span className="font-semibold text-ink">{invite.email}</span>
        </p>
        <p className="text-xs text-muted">Every super admin is notified when you join.</p>
      </div>

      {/* S4: a "Continue with Google" button goes here, above the two choices. */}
      <div className="grid grid-cols-2 gap-2" role="group" aria-label="How do you want to join?">
        {(['signin', 'create'] as const).map((c) => (
          <button
            key={c}
            type="button"
            aria-pressed={choice === c}
            onClick={() => choose(c)}
            className={cn(
              'min-h-[44px] rounded-md border px-2 py-2 text-[13px] font-semibold leading-tight',
              choice === c ? 'border-accent bg-accent-soft text-ink' : 'border-line bg-raised text-ink hover:bg-hover',
            )}
          >
            {c === 'signin' ? 'Sign in' : 'Create an account'}
          </button>
        ))}
      </div>

      {choice === 'signin' ? (
        <form onSubmit={submitSignIn} noValidate className="grid gap-4 border-t border-line pt-4" aria-label="Sign in and join">
          {notice ? <InlineAlert tone="info">{notice}</InlineAlert> : null}
          {wrong ? (
            <InlineAlert
              tone="warn"
              action={
                <Button
                  size="sm"
                  onClick={() => {
                    setWrong(null)
                    setEmail(invite.email)
                    setPassword('')
                    setFocusPw((n) => n + 1)
                  }}
                >
                  Sign in with another account
                </Button>
              }
            >
              This invitation was sent to {invite.email}. You signed in as {wrong}.
            </InlineAlert>
          ) : null}
          {error ? <InlineAlert tone="bad">{error}</InlineAlert> : null}
          <FormField label="Email">
            {({ id, describedBy }) => (
              <TextInput id={id} aria-describedby={describedBy} type="email" autoComplete="username" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={pending} />
            )}
          </FormField>
          <FormField label="Password">
            {({ id, describedBy }) => (
              <TextInput
                ref={passwordRef}
                id={id}
                aria-describedby={describedBy}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={pending}
              />
            )}
          </FormField>
          <div className="-mt-2 flex justify-end">
            <Link to="/forgot-password" state={{ email: email.trim(), fromInvite: location.pathname }} className={authLinkClass}>
              Forgot password?
            </Link>
          </div>
          <Button type="submit" variant="primary" block loading={pending} className="h-10">
            {pending ? 'Joining…' : 'Sign in and join'}
          </Button>
        </form>
      ) : null}

      {choice === 'create' ? (
        <form onSubmit={submitCreate} noValidate className="grid gap-4 border-t border-line pt-4" aria-label="Create an account and join">
          <div className="grid gap-0.5 text-[13px]">
            <span className="text-muted">Your account’s email</span>
            <span className="break-words font-semibold text-ink [overflow-wrap:anywhere]">{invite.email}</span>
          </div>
          {error ? <InlineAlert tone="bad">{error}</InlineAlert> : null}
          <FormField label="Your name" required error={fieldErr.name}>
            {({ id, describedBy, invalid }) => (
              <TextInput id={id} aria-describedby={describedBy} invalid={invalid} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} disabled={pending} />
            )}
          </FormField>
          <FormField label="Password" required error={fieldErr.password} hint="At least 8 characters.">
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={pending}
              />
            )}
          </FormField>
          <Button type="submit" variant="primary" block loading={pending} className="h-10">
            {pending ? 'Joining…' : 'Create account and join'}
          </Button>
        </form>
      ) : null}
    </div>
  )
}
